import { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api, unwrap } from "./api.ts";

export const useMe = () => useQuery({ queryKey: ["me"], queryFn: () => unwrap(api.me.$get()) });
export const useGroups = () => useQuery({ queryKey: ["groups"], queryFn: () => unwrap(api.admin.groups.$get()) });

export const usePublicConfig = () => useQuery({ queryKey: ["public-config"], queryFn: () => unwrap(api.public.config.$get()), staleTime: 60_000 });

/**
 * This install's name, logo and colours (Admin → Branding). The server already put the name in
 * <title>, so that's the fallback until the config arrives — nothing flashes "Krut" first.
 */
const FALLBACK_BRAND = {
  name: document.title || "Krut AI",
  custom: false,
  logoUrl: null as string | null,
  faviconUrl: "/favicon.png",
  accentColor: "#1f7fbf",
  sidebarColor: "#333d4d",
};
export type Brand = typeof FALLBACK_BRAND;
export const useBrand = (): Brand & { loading: boolean } => {
  const q = usePublicConfig();
  return { ...(q.data?.branding ?? FALLBACK_BRAND), loading: q.isPending };
};

/** The value, delayed until it stops changing: for search boxes that query the server. */
export function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * Page / page size / search state for a server-paged list. Changing the search or any `reset` value
 * goes back to page 1; a page that no longer exists (after removing the last row on it) steps back.
 */
export function usePaging(reset: unknown[] = [], initialSize = 25) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialSize);
  const [search, setSearch] = useState("");
  const q = useDebounced(search.trim());
  const key = JSON.stringify([q, pageSize, ...reset]);
  useEffect(() => setPage(1), [key]);
  return {
    page,
    pageSize,
    search,
    q,
    setPage,
    setPageSize,
    setSearch,
    /** Call with the response's total: steps back when the current page is past the end. */
    clamp: (total: number | undefined) => total !== undefined && page > 1 && (page - 1) * pageSize >= total && setPage(Math.max(1, Math.ceil(total / pageSize))),
  };
}

export { keepPreviousData };

/** The person's own preferences (Settings): time zone and live progress in chat apps. */
export const useSettings = () => useQuery({ queryKey: ["settings"], queryFn: () => unwrap(api.me.settings.$get()), staleTime: 60_000 });
